import type { UserPublic } from "@workin/shared";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://workin-api.diamante-rita.workers.dev";

interface AuthState {
  user: UserPublic | null;
  isLoading: boolean;
  isPendingApproval: boolean;
  refetch: () => void;
  updateUser: (updates: Partial<UserPublic>) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserPublic | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPendingApproval, setIsPendingApproval] = useState(false);

  const updateUser = useCallback((updates: Partial<UserPublic>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  }, []);

  const fetchUser = useCallback(async () => {
    setIsLoading(true);
    setIsPendingApproval(false);
    try {
      const me = await api.get<UserPublic & { _newToken?: string }>("/api/users/me");
      if (me._newToken) {
        localStorage.setItem("auth_token", me._newToken);
      }
      const { _newToken: _, ...userData } = me;
      setUser(userData as UserPublic);
    } catch (err: unknown) {
      const e = err as { status?: number };
      if (e?.status === 403) {
        setIsPendingApproval(true);
      } else {
        localStorage.removeItem("auth_token");
        console.error("Failed to fetch user", err);
      }
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const at = hash.get("at");
    if (at) {
      localStorage.setItem("auth_token", at);
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      const finalize = fetch(`${API_BASE}/api/auth/finalize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: at }),
        credentials: "include",
      }).catch(() => {});
      if (localStorage.getItem("oauth_popup") === "1") {
        // Popup OAuth: la finestra principale rileva il token via storage event.
        // Aspetta il finalize prima di chiudere così il cookie viene impostato.
        localStorage.removeItem("oauth_popup");
        finalize.finally(() => window.close());
        return;
      }
      fetchUser();
    } else {
      fetchUser();
    }
  }, [fetchUser]);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "auth_token" && e.newValue && !user) {
        fetchUser();
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [fetchUser, user]);

  return (
    <AuthContext.Provider value={{ user, isLoading, isPendingApproval, refetch: fetchUser, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
